/* eslint-disable no-nested-ternary */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { DASHBOARD_PATH } from 'config';
import { DefaultRootStateProps, NavItemType } from 'types';
import { createMerchantList, getNewRouteType, retryAsync } from 'utils/helpers';
import { RouteObject } from 'react-router-dom';
import MainRoutes from 'routes/MainRoutes';
import { AvailableMenuType, MerchantType } from 'types/components/shared';
import { openSnackbar } from './snackbar';
import { getActiveFieldsService, getTenantIdService, getTermAndProviderService, getUserMulticatalogService } from 'services/auth';

const initialState: DefaultRootStateProps['auth'] = {
    loading: false,
    email: null,
    merchants: null,
    fatherMerchant: null,
    vendor: null,
    activeFields: null,
    menuDetails: [],
    filteredRoutes: [],
    defaultPath: DASHBOARD_PATH,
    idUser: null,
    defaultSelectedMerchant: null,
    tenantId: null,
    isProvider: false,
    termsAndConditions: false,
    idApprovalProfile: null
};

const slice = createSlice({
    name: 'auth',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(getUserMulticatalog.pending, (state) => {
                state.loading = true;
            })
            .addCase(getUserMulticatalog.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getUserMulticatalog.fulfilled, (state, action) => {
                const { merchant, provider, profile, id, idApprovalProfile, email, isProvider } = action.payload.response;
                state.idApprovalProfile = idApprovalProfile;

                const menuDetails = profile?.menuDetails;

                state.idUser = id;
                state.email = email;
                state.isProvider = isProvider;
                // Filtered Routes and Default Path
                if (profile) {
                    state.menuDetails = profile.menuDetails;
                }

                const menuChildren =
                    menuDetails?.flatMap((item: AvailableMenuType) =>
                        item.children.map((child: AvailableMenuType) => getNewRouteType(child.type))
                    ) || [];

                const filteredRoutes: RouteObject[] = MainRoutes.children!.filter((route) =>
                    menuChildren.some((menuItem: NavItemType) => {
                        return route!.path!.includes(`/${menuItem}`);
                    })
                );

                state.filteredRoutes = filteredRoutes;
                const firstFilteredRoute = filteredRoutes[0];
                const defaultRoute = filteredRoutes.find((route: RouteObject) => route.path === DASHBOARD_PATH) || firstFilteredRoute;

                state.defaultPath = defaultRoute?.path || '/';

                // Vendor y Merchants
                if (provider) {
                    state.isProvider = true;
                    state.termsAndConditions = action.payload.response.termsAndConditions;
                }
                const vendorSelected = provider?.idProvider;
                const { name, status, merchantId, fatherMerchantId } = merchant;

                const vendorMerchant = {
                    name,
                    status,
                    merchantId,
                    isFather: !fatherMerchantId
                };

                const defaultMerchantId = Number(localStorage.getItem('defaultMerchantId'));
                let defaultMerchant: MerchantType | undefined;

                if (merchant) {
                    const newMerchants: MerchantType[] =
                        vendorSelected && vendorMerchant.merchantId !== 1 ? [vendorMerchant] : createMerchantList(merchant);

                    state.merchants = [...newMerchants];

                    // FIND FATHER MERCHANT
                    const fatherMerchant = newMerchants.find((merch) => merch.isFather) || null;

                    state.fatherMerchant = fatherMerchant;

                    if (defaultMerchantId) {
                        defaultMerchant = newMerchants.find((item) => item.merchantId === defaultMerchantId && Boolean(item.status));
                    }

                    state.defaultSelectedMerchant = defaultMerchant ?? fatherMerchant;
                }
                if (vendorSelected) {
                    state.vendor = { idMerchant: merchantId, idProvider: vendorSelected };
                }

                const expires = new Date();
                expires.setTime(expires.getTime() + 1 * 86400000);
                document.cookie = `isProvider=${encodeURIComponent(state.isProvider!)}; expires=${expires.toUTCString()}; path=/`;
                document.cookie = `termsAndConditions=${encodeURIComponent(
                    state.termsAndConditions!
                )}; expires=${expires.toUTCString()}; path=/`;

                state.loading = false;
            });
        // ACTIVE FIELDS
        builder
            .addCase(getActiveFields.pending, (state) => {
                state.loading = true;
            })
            .addCase(getActiveFields.fulfilled, (state, action) => {
                state.activeFields = action.payload;
                state.loading = false;
            })
            .addCase(getTenantId.pending, (state) => {
                state.loading = true;
            })
            .addCase(getTenantId.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getTenantId.fulfilled, (state, action) => {
                state.loading = false;
                state.tenantId = action.payload;

                localStorage.setItem('tenantId', action.payload);
            })
            .addCase(getTermsandProvider.pending, (state) => {
                state.loading = true;
            })
            .addCase(getTermsandProvider.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getTermsandProvider.fulfilled, (state, action) => {
                state.loading = false;
                const expires = new Date();
                expires.setTime(expires.getTime() + 1 * 86400000);
                document.cookie = `isProvider=${encodeURIComponent(action.payload.isProvider)}; expires=${expires.toUTCString()}; path=/`;
                document.cookie = `termsAndConditions=${encodeURIComponent(
                    action.payload.termsAndConditions
                )}; expires=${expires.toUTCString()}; path=/`;
            });
    },
    reducers: {
        logoutState(state) {
            state = initialState;
        },
        cleanActiveFields(state, action) {
            state.activeFields = null;
            state.vendor = null;
        },
        updateGlobalMerchant(state, action: PayloadAction<MerchantType>) {
            state.defaultSelectedMerchant = action.payload;
            localStorage.setItem('defaultMerchantId', `${action.payload.merchantId}`);
        },
        setTenantId(state, action: PayloadAction<string>) {
            state.tenantId = action.payload;
            localStorage.setItem('tenantId', action.payload);
        }
    }
});

export const getUserMulticatalog = createAsyncThunk(`${slice.name}/getMerchants`, async (user: string | null) => {
    const getProfile = async (): Promise<any> => {
        const { data } = await getUserMulticatalogService({ user });

        const email: string = (data.response.email ?? '').toLowerCase();

        const profile = data.response.profile;
        const userEmail = user?.toLowerCase();

        if (email !== userEmail) throw new Error('Invalid email');
        if (!profile) throw new Error('Invalid profile');

        return data;
    };

    return await retryAsync(getProfile, 3);
});

export const getActiveFields = createAsyncThunk(
    `${slice.name}/getActiveFields`,
    async ({ idMerchant, idProvider }: { idMerchant: number; idProvider: number }) => {
        try {
            const resp = await getActiveFieldsService({ idMerchant, idProvider });

            return resp;
        } catch (err: any) {
            throw err;
        }
    }
);

export const getTenantId = createAsyncThunk<string, void>(`${slice.name}/getTenantId`, async (_args, { dispatch }) => {
    try {
        const host = window.location.hostname;

        if (host === 'localhost') {
            return 'omx0-9lrtq';
        }
        const tenant = host.split('.')[0];

        const tenantId = await retryAsync(() => getTenantIdService({ tenant }), 2);

        return tenantId as string;
    } catch (err) {
        dispatch(
            openSnackbar({
                message: 'An error occurred fetching tenantId, contact your administrator.',
                variant: 'error'
            })
        );
        return ''; // siempre retornas string
    }
});

export const getTermsandProvider = createAsyncThunk(`${slice.name}/getTermsandProvider`, async () => {
    const { data } = await getTermAndProviderService();

    return data.response;
});

export const { cleanActiveFields, updateGlobalMerchant, logoutState, setTenantId } = slice.actions;

// Reducer
export default slice.reducer;
